// «Crear una alerta › En tus palabras» (Ola 3, frente F4), sin React: el `Draft` que devuelve
// `POST agency/alert-rules/draft-from-text/` (contrato §2) → el MISMO borrador que edita el constructor
// de F2 (`rulesModel.emptyDraft`), más qué chips resaltar y por qué.
//
// Tres casos por campo, y no se mezclan:
// - Vino en el borrador → se carga y el chip se resalta como interpretado (`-ai`).
// - Quedó en duda (`clarifications`) → el campo queda VACÍO y el chip lleva el motivo: la validación
//   local de F2 no deja guardar hasta completarlo. Un default ahí sería guardar algo que nadie dijo.
// - No se dijo → el default del constructor (correo, a mí, el período de la línea), sin resaltar.
import {
    CHANNEL_EMAIL,
    CHANNEL_WEBHOOK,
    emptyDraft,
    formatInputNumber,
    OPERATORS,
    RECIPIENT_TYPES,
    RECIPIENTS_USERS,
    SCOPE_ANY,
    SCOPE_CLIENT,
    SCOPE_TYPES,
} from './rulesModel';

export const MARK_AI = 'ai';
export const MARK_ASK = 'ask';

export const NL_MIN_LENGTH = 10;
export const NL_MAX_LENGTH = 500;

// El campo del borrador (y de los errores de F2) que corresponde a cada `field` del contrato.
const DRAFT_FIELD_BY_CLARIFICATION = {
    channel: 'channels',
    recipients: 'recipients',
    scope: 'scope',
    metric: 'metric',
    operator: 'operator',
    value: 'value',
    period: 'period',
};

// El texto de cada motivo. Uno que no esté acá (un backend más nuevo) cae al genérico: la pantalla
// nunca muestra el código crudo.
const CLARIFY_KEYS = {
    AMBIGUOUS_CLIENT: 'alerts_center.nl.clarify_ambiguous_client',
    UNKNOWN_CLIENT: 'alerts_center.nl.clarify_unknown_client',
    AMBIGUOUS_LINE: 'alerts_center.nl.clarify_ambiguous_line',
    UNKNOWN_LINE: 'alerts_center.nl.clarify_unknown_line',
    AMBIGUOUS_LABEL: 'alerts_center.nl.clarify_ambiguous_label',
    UNKNOWN_LABEL: 'alerts_center.nl.clarify_unknown_label',
    MISSING_SCOPE: 'alerts_center.nl.clarify_missing_scope',
    UNKNOWN_METRIC: 'alerts_center.nl.clarify_unknown_metric',
    MISSING_OPERATOR: 'alerts_center.nl.clarify_missing_operator',
    UNSUPPORTED_CONDITION: 'alerts_center.nl.clarify_unsupported_condition',
    MISSING_VALUE: 'alerts_center.nl.clarify_missing_value',
    UNKNOWN_PERIOD: 'alerts_center.nl.clarify_unknown_period',
    UNKNOWN_RECIPIENT: 'alerts_center.nl.clarify_unknown_recipient',
    UNSUPPORTED_CHANNEL: 'alerts_center.nl.clarify_unsupported_channel',
    UNIT_MISMATCH: 'alerts_center.nl.clarify_unit_mismatch',
};
export const CLARIFY_GENERIC_KEY = 'alerts_center.nl.clarify_generic';

export function clarifyKey(reason) {
    return CLARIFY_KEYS[reason] || CLARIFY_GENERIC_KEY;
}

// Los errores de `draft-from-text` que la pantalla distingue. Cada uno dice qué hacer: «prueba con
// otra frase» sólo sirve cuando el problema ES la frase (review C-ac-nl-14).
export const NL_ERROR_NO_MODULE = 'NO_MODULE_ACCESS';
export const NL_ERROR_TEXT = 'INVALID_VALUE';
export const NL_ERROR_UNAVAILABLE = 'NL_UNAVAILABLE';
export const NL_ERROR_NO_PERMISSION = 'ERROR_USER_DOES_NOT_HAVE_ACCESS';

export function nlErrorKey(response) {
    const code = response && (response.error_code || response.status);
    const httpStatus = response && response.http_status;
    if (code === NL_ERROR_NO_MODULE) return 'alerts_center.nl.no_module';
    if (code === NL_ERROR_TEXT) return 'alerts_center.nl.text_length';
    if (code === NL_ERROR_NO_PERMISSION || httpStatus === 403) return 'alerts_center.no_edit_permission';
    if (httpStatus === 429) return 'alerts_center.nl.too_many';
    if (code === NL_ERROR_UNAVAILABLE || code === 'NETWORK_ERROR' || httpStatus >= 500) {
        return 'alerts_center.nl.unavailable';
    }
    return 'alerts_center.nl.error';
}

export function canConvert(text) {
    const length = (text || '').trim().length;
    return length >= NL_MIN_LENGTH && length <= NL_MAX_LENGTH;
}

function idsOf(list) {
    return Array.isArray(list) ? list.map(Number).filter((id) => Number.isFinite(id)) : [];
}

/**
 * `{draft, marks}`: el borrador del constructor y, por campo, `{kind: 'ai'}` o
 * `{kind: 'ask', reason, said, options}`.
 */
export function draftFromNaturalLanguage(result, {locale = 'es'} = {}) {
    const base = emptyDraft();
    const proposal = (result && result.draft) || {};
    const condition = proposal.condition || {};
    const scope = proposal.scope || null;
    const delivery = proposal.delivery || {};
    const marks = {};
    const draft = {...base};

    const asks = {};
    ((result && result.clarifications) || []).forEach((clarification) => {
        const field = DRAFT_FIELD_BY_CLARIFICATION[clarification && clarification.field];
        if (field && !asks[field]) asks[field] = clarification;
    });
    const ask = (field, extra = {}) => {
        const clarification = asks[field];
        marks[field] = {kind: MARK_ASK, reason: clarification.reason, said: clarification.said || null,
            options: Array.isArray(clarification.options) ? clarification.options : [], ...extra};
    };
    // Un campo de la condición que no vino y no trae aclaración (un backend que no la mandó) se pide
    // igual: el default del constructor (`LT`) sería guardar algo que nadie dijo (review C-ac-nl-21).
    const askMissing = (field, reason) => {
        marks[field] = {kind: MARK_ASK, reason, said: null, options: []};
    };
    const interpreted = (field) => { marks[field] = {kind: MARK_AI}; };

    // Canal: con aclaración (WhatsApp, o el webhook que el plan no tiene) queda VACÍO, como el resto de
    // los campos en duda, así no se puede guardar sin elegirlo (review C-ac-nl-15).
    if (asks.channels) {
        draft.channels = [];
        ask('channels');
    } else if (Array.isArray(delivery.channels) && delivery.channels.length) {
        draft.channels = delivery.channels.filter((c) => c === CHANNEL_EMAIL || c === CHANNEL_WEBHOOK);
        interpreted('channels');
    }

    // A quién: una persona que no se encontró deja «a estas personas» sin nadie, para elegirla.
    if (asks.recipients) {
        draft.recipients = {type: RECIPIENTS_USERS, userIds: []};
        ask('recipients');
    } else if (delivery.recipients && RECIPIENT_TYPES.includes(delivery.recipients.type)) {
        draft.recipients = {type: delivery.recipients.type, userIds: idsOf(delivery.recipients.user_ids)};
        interpreted('recipients');
    }

    // Sobre qué
    const scopeType = scope && SCOPE_TYPES.includes(scope.type) ? scope.type : SCOPE_CLIENT;
    if (asks.scope) {
        draft.scope = {type: scopeType, ids: []};
        ask('scope', {scopeType});
    } else if (scope && (scope.type === SCOPE_ANY || idsOf(scope.ids).length)) {
        draft.scope = {type: scopeType, ids: scope.type === SCOPE_ANY ? [] : idsOf(scope.ids)};
        interpreted('scope');
    }

    // La condición
    if (asks.metric) {
        draft.metric = null;
        ask('metric');
    } else if (condition.metric) {
        draft.metric = condition.metric;
        interpreted('metric');
    }
    if (asks.operator) {
        draft.operator = null;
        ask('operator');
    } else if (OPERATORS.includes(condition.operator)) {
        draft.operator = condition.operator;
        interpreted('operator');
    } else {
        draft.operator = null;
        askMissing('operator', 'MISSING_OPERATOR');
    }
    if (asks.value) {
        draft.value = '';
        ask('value');
    } else if (typeof condition.value === 'number') {
        draft.value = formatInputNumber(condition.value, locale);
        interpreted('value');
    } else if (!(marks.operator && marks.operator.reason === 'UNSUPPORTED_CONDITION')) {
        askMissing('value', 'MISSING_VALUE');
    }
    if (asks.period) {
        draft.period = null;
        ask('period');
    } else if (condition.period) {
        draft.period = condition.period;
        interpreted('period');
    }

    return {draft, marks};
}

/**
 * Las marcas con la foto de cada campo tal como lo dejó la conversión. Así se sabe después si el
 * usuario lo tocó sin comparar contra un borrador «anterior», que en un render puede estar viejo
 * (review C-ac-nl-04 de la ronda 2).
 */
export function stampMarks(marks, draft) {
    const stamped = {};
    Object.entries(marks || {}).forEach(([field, mark]) => {
        stamped[field] = {...mark, snapshot: JSON.stringify(draft[field])};
    });
    return stamped;
}

/** Las marcas que siguen valiendo: lo que el usuario corrigió a mano deja de estar «interpretado» o «en duda». */
export function liveMarks(marks, draft) {
    const live = {};
    Object.entries(marks || {}).forEach(([field, mark]) => {
        if (mark.snapshot === undefined || mark.snapshot === JSON.stringify(draft[field])) live[field] = mark;
    });
    return live;
}

/** Elegir uno de los candidatos de una aclaración ambigua (cliente, línea o etiqueta). */
export function chooseScopeOption(draft, optionId) {
    return {...draft, scope: {...draft.scope, ids: [Number(optionId)]}};
}

// Qué tipo de candidato corresponde a cada alcance. Para una LÍNEA nunca se ofrece un cliente: su id
// elegido como id de línea era una regla en otra línea (review C-ac-nl-01).
const CANDIDATE_REASON_SUFFIX = {CLIENT: '_CLIENT', LINE: '_LINE', LINES_WITH_LABEL: '_LABEL'};

/** Las aclaraciones que se pueden resolver con un clic: un alcance con candidatos de SU tipo. */
export function scopeChoices(marks) {
    const mark = marks && marks.scope;
    if (!mark || mark.kind !== MARK_ASK || !mark.options || !mark.options.length) return [];
    const suffix = CANDIDATE_REASON_SUFFIX[mark.scopeType];
    return suffix && String(mark.reason || '').endsWith(suffix) ? mark.options : [];
}

// A qué campo se refiere cada nota de `interpreted_as`, por su rótulo (las arma el MCP y el Backend en
// español: «métrica: «gasto» → Costo»). Sirve para dejar de mostrar la de un campo que el usuario ya
// corrigió (review C-ac-nl-16).
const NOTE_FIELD_BY_PREFIX = [
    ['cliente:', 'scope'], ['línea de seguimiento:', 'scope'], ['etiqueta:', 'scope'],
    ['métrica:', 'metric'], ['período:', 'period'], ['destinatario:', 'recipients'],
];

/**
 * Las notas de «Entendí esto» que siguen valiendo: sólo en español (vienen escritas en español; en
 * otro idioma no se muestran) y sólo las de campos que siguen interpretados.
 */
export function visibleNotes(notes, marks, language = 'es') {
    if (!String(language || '').toLowerCase().startsWith('es')) return [];
    return (notes || []).filter((note) => {
        const entry = NOTE_FIELD_BY_PREFIX.find(([prefix]) => String(note).startsWith(prefix));
        return !entry || (marks && marks[entry[1]] && marks[entry[1]].kind === MARK_AI);
    });
}
